/**
 * Reusable profile modal (D1) — view a user's profile, and edit it when it's the
 * current user's own.
 *
 * View mode shows the avatar + name + bio (read-only). When the viewed profile is
 * one the current user may edit (their own), an "Edit profile" button reveals the
 * shared {@link ProfileEditor} — the SAME avatar/name/bio form the Synthase
 * settings panel uses, so there is one source of truth for profile editing and no
 * second divergent editor. It is opened from two places:
 *
 *   - the top-bar profile button (D2) — own profile, opened straight to `edit`;
 *   - a click on any author's avatar or name in the Synthase chat (C5) — opened
 *     to `view`. The viewer's OWN messages open their own profile (with "Edit
 *     profile" + "Log out"); a TEAMMATE's open theirs, view-only, via `profile`
 *     (seeded from the message's author snapshot, enriched by the host with the
 *     live bio from `GET /projects/:id/team/profile/:userId`).
 *
 * @module
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import { resolveUserAvatar } from '@molecule/app-ide-react'
import { useAuth, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Avatar, Button, Flex, Modal } from '@molecule/app-ui-react'

import { ProfileEditor, type ProfileUser } from './ProfileEditor.js'

/** The subset of a user's profile this modal displays. */
export interface ProfileView {
  name?: string | null
  email?: string | null
  avatar?: string | null
  bio?: string | null
}

/** Props for {@link ProfileModal}. */
export interface ProfileModalProps {
  open: boolean
  onClose: () => void
  /**
   * The profile to display. Omit it to show the signed-in user's OWN profile
   * (which is editable). Pass an explicit profile (a teammate whose avatar/name
   * was clicked in the chat) to show theirs — view-only unless
   * {@link ProfileModalProps.editable} says otherwise.
   */
  profile?: ProfileView
  /**
   * Whether the current user may edit the viewed profile. Defaults to `true` when
   * no `profile` is passed (the own profile), `false` otherwise.
   */
  editable?: boolean
  /**
   * Which mode to open in. The top-bar profile button opens its own profile
   * straight to `'edit'`; an avatar click opens `'view'` (with the edit button
   * present when editable). Defaults to `'view'`.
   */
  initialMode?: 'view' | 'edit'
}

/**
 * Renders the profile view/edit modal.
 *
 * @param props - {@link ProfileModalProps}.
 * @returns The modal (renders nothing until `open`).
 */
export function ProfileModal({
  open,
  onClose,
  profile,
  editable,
  initialMode = 'view',
}: ProfileModalProps) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const { user, logout } = useAuth<ProfileUser>()
  const navigate = useNavigate()

  // Own profile = no explicit `profile` passed → the signed-in user, editable.
  const isOwn = !profile
  const canEdit = editable ?? isOwn
  // An explicit `profile` (a future collaborator) wins; otherwise show the live
  // auth user, so the own-profile view reflects edits made in this modal without
  // a refetch. `{}` is the safe empty fallback before auth resolves.
  const view: ProfileView = profile ?? user ?? {}

  const [mode, setMode] = useState<'view' | 'edit'>(initialMode)
  // Reset to the requested mode each time the modal (re)opens; never start in
  // edit mode for a profile the user can't edit.
  useEffect(() => {
    if (open) setMode(canEdit ? initialMode : 'view')
  }, [open, initialMode, canEdit])

  // Same flow as the settings panel's logout: end the session, close, and leave
  // the (now inaccessible) authenticated page.
  const handleLogout = async (): Promise<void> => {
    await logout()
    onClose()
    navigate('/login')
  }

  // Own profile only (never on a collaborator's). Rendered at the RIGHT of the
  // bottom action row in both modes — edit mode via ProfileEditor's
  // secondaryAction slot (Save moves left), view mode next to Edit profile.
  const logoutButton = isOwn ? (
    // Primary (blue), matching the settings panel's Log out — routine, not
    // destructive; red is reserved for Delete account.
    <Button size="sm" data-mol-id="profile-logout" onClick={() => void handleLogout()}>
      {t('settings.logOut', undefined, { defaultValue: 'Log out' })}
    </Button>
  ) : null

  const avatarSrc = resolveUserAvatar(view.avatar) ?? undefined
  const displayName =
    view.name?.trim() || view.email || t('profile.anonymous', undefined, { defaultValue: 'User' })
  const title = isOwn
    ? t('profile.titleOwn', undefined, { defaultValue: 'Your profile' })
    : displayName

  return (
    <Modal open={open} onClose={onClose} size="sm" title={title} data-mol-id="profile-modal">
      {mode === 'edit' && canEdit ? (
        // No onSaved→view flip: edits AUTO-save on a debounce now, so
        // switching modes on save would yank the form away mid-typing.
        <ProfileEditor secondaryAction={logoutButton} />
      ) : (
        <div className={cm.stack(4)}>
          <Flex align="center" gap="md">
            <Avatar
              src={avatarSrc}
              name={displayName}
              size={72}
              alt={t('profile.avatarAlt', { name: displayName }, { defaultValue: 'Avatar' })}
              testId="profile-view-avatar"
            />
            <div className={cm.stack(1)}>
              <div
                className={cm.textSize('lg')}
                style={{ fontWeight: 600 }}
                data-mol-id="profile-view-name"
              >
                {displayName}
              </div>
              {view.email && (
                <div className={cm.cn(cm.textSize('sm'), cm.textMuted)}>{view.email}</div>
              )}
            </div>
          </Flex>

          <div>
            <div className={cm.formLabelSmall}>
              {t('profile.bioLabel', undefined, { defaultValue: 'Bio' })}
            </div>
            {view.bio?.trim() ? (
              <p
                className={cm.textSize('sm')}
                style={{ whiteSpace: 'pre-wrap', margin: 0 }}
                data-mol-id="profile-view-bio"
              >
                {view.bio}
              </p>
            ) : (
              <p className={cm.cn(cm.textSize('sm'), cm.textMuted)} style={{ margin: 0 }}>
                {t('profile.noBio', undefined, { defaultValue: 'No bio yet.' })}
              </p>
            )}
          </div>

          {(canEdit || logoutButton) && (
            <Flex justify="between" align="center">
              {/* Left slot mirrors edit mode's Save placement; empty div keeps
                  Log out pinned right when there's no Edit button. */}
              <div>
                {canEdit && (
                  <Button size="sm" data-mol-id="profile-edit" onClick={() => setMode('edit')}>
                    {t('profile.editProfile', undefined, { defaultValue: 'Edit profile' })}
                  </Button>
                )}
              </div>
              {logoutButton}
            </Flex>
          )}
        </div>
      )}
    </Modal>
  )
}

ProfileModal.displayName = 'ProfileModal'
